/**
 * Sizes the app to the area the user can actually see.
 *
 * - Keyboard: on iOS it does not resize the layout viewport, it overlays the page. While a text field is
 *   focused and the visual viewport shrank, the app is pinned to the visual viewport (top + height), so the
 *   composer sits right above the keyboard.
 * - Installed iOS PWA (standalone) with a translucent status bar: WebKit makes the viewport shorter than
 *   the screen by the status-bar height and does not paint fixed content in the strip left at the bottom
 *   (verified on iPhone: content stretched into it was cut off). Only the page background shows there, so
 *   the app keeps the viewport height, the background is coloured like the current screen (styles.css,
 *   `html:has(...)`), and the bottom safe-area padding is dropped (`ios-short-viewport`): the home
 *   indicator already lies inside that strip.
 * - Everywhere else the CSS fallback (100dvh) is used.
 */
export function trackVisualViewport() {
  const vv = window.visualViewport;
  if (!vv) return;
  const root = document.documentElement;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const landscape = window.matchMedia('(orientation: landscape)');
  // iOS does not swap screen.width/height on rotation.
  const screenHeight = () =>
    landscape.matches ? Math.min(screen.width, screen.height) : Math.max(screen.width, screen.height);

  let baseline = vv.height; // visual viewport height with the keyboard closed
  let width = vv.width;
  let frame = 0;

  const update = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      if (Math.abs(vv.width - width) > 1) {
        // Rotation or window resize: start over.
        width = vv.width;
        baseline = vv.height;
      }
      // The keyboard can only be open while a text field has focus; otherwise re-measure the baseline.
      const el = document.activeElement as HTMLElement | null;
      const editing = !!el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable);
      baseline = editing ? Math.max(baseline, vv.height) : vv.height;
      const keyboardOpen = editing && baseline - vv.height > 120;

      // Standalone iOS viewport shorter than the screen by the status bar (capped: iPad split view etc.).
      const missing = iosStandalone ? screenHeight() - baseline : 0;
      root.classList.toggle('ios-short-viewport', missing > 0 && missing <= 80);

      root.classList.toggle('keyboard-open', keyboardOpen);
      if (keyboardOpen) {
        root.style.setProperty('--app-height', `${vv.height}px`);
        root.style.setProperty('--app-top', `${vv.offsetTop}px`);
      } else {
        root.style.removeProperty('--app-height');
        root.style.removeProperty('--app-top');
      }
    });
  };
  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  landscape.addEventListener('change', update);
  document.addEventListener('focusin', update);
  document.addEventListener('focusout', update);
  update();
}

